/**
 * @file movements-period.ts
 * @description Период журнала движений: 7 / 30 / 90 дней или свой диапазон
 * (phase19.24 этап 1, шаг 2).
 *
 * Почему не `wms-period.ts`: там реестр журналов ДОКУМЕНТОВ (30 / 90 / всё
 * время) без верхней границы — документ, созданный секунду назад, обязан
 * попасть в выдачу. Журнал движений живёт иначе: окно ограничено лимитом 500
 * строк, поэтому нужен и короткий период «7 дней», и свой диапазон с обеими
 * границами — «покажи, что было 3 сентября». Смешивать два реестра нельзя:
 * добавив «7 дней» в общий, мы поменяли бы три журнала документов заодно.
 *
 * Границы считаем по местному времени пользователя: `2026-09-03` в поле «с» —
 * это начало суток 3 сентября у кладовщика, а не UTC-полночь (в Челябинске
 * разница 5 часов, иначе утренние движения выпадали бы из отбора).
 */

/** Значение селекта «Период». Строки — `SelectField` работает со строками. */
export type MovementsPeriod = "7" | "30" | "90" | "custom";

/** По умолчанию — месяц: неделя слишком коротка для разбора «откуда минус». */
export const MOVEMENTS_PERIOD_DEFAULT: MovementsPeriod = "30";

/** Варианты селекта — реестр, а не литералы по месту отрисовки. */
export const MOVEMENTS_PERIOD_OPTIONS: {
  value: MovementsPeriod;
  label: string;
}[] = [
  { value: "7", label: "7 дней" },
  { value: "30", label: "30 дней" },
  { value: "90", label: "90 дней" },
  { value: "custom", label: "Свой период" },
];

/** Сколько дней назад отсчитывать; `custom` — границы берём из полей. */
const PERIOD_DAYS: Record<MovementsPeriod, number | null> = {
  "7": 7,
  "30": 30,
  "90": 90,
  custom: null,
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Границы отбора для query `from` / `to`; пустое поле — границы нет. */
export interface MovementsRange {
  from?: string;
  to?: string;
}

/** Свой диапазон из полей формы в формате `<input type="date">`. */
export interface MovementsCustomRange {
  from: string;
  to: string;
}

/** `2026-09-03` + время суток → ISO; пустая или битая дата — `undefined`. */
function dayBoundary(day: string, endOfDay: boolean): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const date = new Date(`${day}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * Границы периода для запроса журнала.
 *
 * @param period период из селекта;
 * @param custom значения полей «с» / «по» (нужны только для `custom`);
 * @param now    точка отсчёта (в тестах — фиксированная дата).
 */
export function movementsPeriodRange(
  period: MovementsPeriod,
  custom: MovementsCustomRange = { from: "", to: "" },
  now: Date = new Date(),
): MovementsRange {
  if (period === "custom") {
    return {
      from: dayBoundary(custom.from, false),
      to: dayBoundary(custom.to, true),
    };
  }
  const days = PERIOD_DAYS[period] ?? PERIOD_DAYS[MOVEMENTS_PERIOD_DEFAULT];
  // Верхнюю границу у пресетов не шлём: «последние N дней» заканчиваются
  // «сейчас», а фиксированный `to` отрезал бы только что проведённый документ.
  return { from: new Date(now.getTime() - (days ?? 30) * DAY_MS).toISOString() };
}

/** Значение `?period=` из URL; всё неизвестное — период по умолчанию. */
export function parseMovementsPeriod(raw: string | null): MovementsPeriod {
  const found = MOVEMENTS_PERIOD_OPTIONS.find((item) => item.value === raw);
  return found ? found.value : MOVEMENTS_PERIOD_DEFAULT;
}

/** Подпись периода для пустого состояния: «за последние 30 дней». */
export function movementsPeriodLabel(period: MovementsPeriod): string {
  const option = MOVEMENTS_PERIOD_OPTIONS.find((item) => item.value === period);
  return option ? option.label.toLowerCase() : "выбранный период";
}
